import React, { useState } from 'react';

const TopDriModal = ({ show, onClose, onCloseAll }) => {
  const [currentSection, setCurrentSection] = useState(0);

  if (!show) return null;

  // Table of Contents
  const sections = [
    'What is CONTAINER RAIN and what causes it?',
    'Solution',
    'Top Dri Advantages',
    'Success Stories',
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl h-[90vh] flex flex-col md:flex-row">
        {/* Sidebar - Table of Contents */}
        {/* Mobile Dropdown (Styled Dropdown for Small Screens) */}
        <div className="md:hidden w-full p-4 bg-[#f8fafc] border-b shadow-sm flex justify-center">
          <div className="relative w-full max-w-xs">
            <select 
              className="w-full p-3 bg-white border border-gray-300 rounded-lg shadow-md text-gray-700 text-lg font-medium focus:outline-none focus:ring-2 focus:ring-[#0a354a]"
              onChange={(e) => setCurrentSection(Number(e.target.value))}
              value={currentSection}
            >
              {sections.map((title, index) => (
                <option key={index} value={index} className="text-gray-700">
                  {title}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Desktop Sidebar (Hidden on Mobile) */}
        <div className="hidden md:block w-1/6 bg-gray-100 p-6 border-r rounded-l-lg">
          <ul>
            {sections.map((title, index) => (
              <div key={index}>
                <li
                  className={`py-2 cursor-pointer ${
                    currentSection === index ? 'font-bold text-[#0a354a]' : 'text-gray-600'
                  }`}
                  onClick={() => setCurrentSection(index)}
                >
                  {title}
                </li>
                {index < sections.length - 1 && ( // Avoids adding a line after the last item
                  <hr className="border-t border-gray-300 my-2" />
                )}
              </div>
            ))}
          </ul>
        </div>

        {/* Main Content - Scrollable */}
        <div className="w-full md:w-5/6 p-6 overflow-y-auto relative rounded-r-lg">
        
          {/* Close Button (INSIDE the modal container) */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
          
          <h2 className="text-2xl font-semibold text-[#0a354a]">{sections[currentSection]}</h2>

          {/* Section 1: What is CONTAINER RAIN and what causes it? */}
          {currentSection === 0 && (
            <div>
              <p className="text-gray-700 mt-4 leading-relaxed text-justify">
                Container rain occurs during <b>long-haul transport</b> with varying climate and temperature. 
                This results in <b>condensation</b> inside the vessel, which can cause damage to goods.
              </p>

              <p className="text-gray-700 leading-relaxed mt-4 text-justify">
                External climatic conditions play a major role in container environments. The likelihood of condensation 
                is influenced by the transport <b>route, season, time of day, and weather (rainy, sunny, etc.)</b>
              </p>

              {/* Two-column layout */}
              <div className="flex flex-col md:flex-row mt-6 gap-6">
                
                {/* Left Side: Causes List */}
                <div className="w-full md:w-2/3">
                  <h3 className="text-lg font-semibold">Common Causes of Container Rain:</h3>
                  <ul className="list-disc pl-6 text-gray-700 mt-2 space-y-2">
                    <li>High water content in the air inside the container.</li>
                    <li>Moisture in packaging, dunnage, or pallets.</li>
                    <li>Water content in cargo or stored goods.</li>
                    <li>Rainfall exposure during the loading process.</li>
                    <li>Water penetration due to damaged or unsealed containers.</li>
                    <li>Temperature fluctuations causing condensation.</li>
                  </ul>
                </div>

                {/* Right Side: Condensation Image */}
                <div className="w-full flex justify-center mt-4 md:mt-0">
                  <img 
                    src="./assets/imgs/condensation.png" 
                    alt="Condensation inside a container" 
                    className="w-[90%] md:w-[250px] h-auto rounded-lg shadow-md"
                  />
                </div>
              </div>

              {/* Final Explanation */}
              <p className="text-gray-700 mt-6 leading-relaxed text-justify">
                Container rain is <b>worsened by temperature and humidity changes</b> during shipping. 
                During <b>daytime</b>, heat causes water vapor to rise. At <b>night</b>, when temperatures drop, 
                condensation occurs, leading to <b>sweat accumulation on ceilings</b> and dripping moisture.
              </p>
            </div>
          )}

          {/* Section 2: TOP DRI Solutions */}
          {currentSection === 1 && (
            <div>
                {/* Solution Header */}
                <p className="text-gray-700 mt-4 leading-relaxed text-justify">
                The solution is to keep the environment dry within the shipping container. Universal Desiccants Corporation specifically formulated
                the <b>Top-Dri Container Desiccant</b> to combat condensation during long-haul transport via air, sea, or land.
                </p>

                {/* Two-column layout */}
                <div className="flex flex-col md:flex-row items-center md:items-start mt-6 gap-6">
                
                {/* Left Side: Product Description */}
                <div className="w-full md:w-2/3 bg-blue-100 p-4 rounded-lg shadow-md">
                    <h3 className="font-bold text-[#0a354a] text-lg">Top-Dri™</h3>
                    <p className="text-gray-700 leading-relaxed mt-2 text-justify">
                    Top-Dri is designed to absorb moisture up to <b>more than 300% of its own weight</b>. With its unique formulation and proprietary 
                    packaging, the water vapor molecules absorbed are transformed into gel, making it impossible for the trapped moisture to spill or re-evaporate.
                    </p>
                </div>

                {/* Right Side: Product Image */}
                <div className="w-full md:w-1/3 flex justify-center">
                    <img 
                    src="./assets/imgs/UDC-38.jpg" 
                    alt="Top-Dri Container Desiccant" 
                    className="w-full md:max-w-[300px] h-auto rounded-lg shadow-md"
                    />
                </div>
                </div>

                {/* Industry Applications CTA */}
                <div className="mt-8 text-center">
                <p className="text-gray-700">
                    Want to learn more about <b>Industry Applications</b>? Click below:
                </p>
                <a 
                  href="#applications" 
                  onClick={(e) => {
                    e.preventDefault(); // Prevent default navigation
                    if (onCloseAll) onCloseAll(); // Close both modals
                    setTimeout(() => {
                      document.getElementById("applications")?.scrollIntoView({ behavior: "smooth" });
                    }, 300);
                  }}
                  className="text-[#0a354a] font-semibold hover:underline mt-2 inline-block"
                >
                  Explore Industry Applications
                </a>

                </div>
            </div>
            )}

          {/* Section 3: TOP DRI Advantages*/}
          {currentSection === 2 && (
            <div>
                {/* Introduction */}
                <p className="text-gray-700 mt-4 leading-relaxed text-justify">
                <b>Top-Dri</b> quickly reduces shipping container humidity to a predetermined lower level 
                and keeps it there for an extended period. Most importantly, it is <b>environmentally friendly</b>, 
                making disposal hassle-free.
                </p>

                {/* No-Sweat Application (Green Box) */}
                <div className="bg-blue-100 text-black p-4 rounded-lg mt-6 shadow-md">
                <h3 className="text-lg font-bold">No-Sweat Application:</h3>
                <p className="mt-2">
                    Just place <b>Top-Dri</b> on top of carton boxes or cargo.  
                </p>
                <p className="mt-1">
                    Its special feature keeps it in place without leaving ugly peel marks.
                </p>
                </div>

                {/* Two-column Layout */}
                <div className="flex flex-col md:flex-row mt-6 gap-6">
                
                {/* Left Side: Recommended Application */}
                <div className="w-full md:w-1/3 bg-gray-100 p-4 rounded-lg shadow-md">
                    <h3 className="text-lg font-bold text-[#0a354a]">Recommended Application</h3>
                    <ul className="text-gray-700 mt-2">
                    <li><b>20’ container:</b> 32 pcs.</li>
                    <li><b>40’ container:</b> 64 pcs.</li>
                    </ul>
                    <p className="text-gray-600 mt-2 text-justify">
                    Some cargoes may need special application. Contact us for assistance.
                    </p>
                </div>

                {/* Right Side: Images */}
                <div className="w-full md:w-2/3 flex justify-center gap-4">
                    <img 
                    src="./assets/imgs/advantages-1.png" 
                    alt="Top-Dri placed on cargo" 
                    className="w-1/2 md:w-[210px] h-auto rounded-lg shadow-md"
                    />
                    <img 
                    src="./assets/imgs/advantages-2.png" 
                    alt="Top-Dri in shipping container" 
                    className="w-1/2 md:w-[210px] h-auto rounded-lg shadow-md"
                    />
                </div>
                </div>
            </div>
            )}
          
          {/* Section 4: TOP DRI Success Stories*/}
          {currentSection === 3 && (
            <div>
                {/* Two-column layout */}
                <div className="flex flex-col md:flex-row mt-6 gap-6">

                {/* Left Column: Testimonials & Images */}
                <div className="w-full md:w-1/2">
                <h3 className="text-lg font-semibold text-[#0a354a]">WHAT OUR CUSTOMERS SAY:</h3>

                    <div className="space-y-4">
                    <p className="text-gray-700 mt-6 leading-relaxed bg-gray-100 p-4 rounded-md shadow text-justify">
                    <i>“We have eliminated costly claims by hundreds of thousands of dollars.”</i>
                    </p>
                    <p className="text-gray-700 mt-6 leading-relaxed bg-gray-100 p-4 rounded-md shadow text-justify">
                    <i>“Our product integrity is maintained, making our customers satisfied.”</i>
                    </p>
                    <p className="text-gray-700 mt-6 leading-relaxed bg-gray-100 p-4 rounded-md shadow text-justify">
                    <i>“Our customers are pleased by the efforts we put into protecting our cargoes against damage.”</i>
                    </p>
                    <p className="text-gray-700 mt-6 leading-relaxed bg-gray-100 p-4 rounded-md shadow text-justify">
                    <i>“We finally got the contract when we used Top-Dri in our shipment.”</i>
                    </p>
                    <p className="text-gray-700 mt-6 leading-relaxed bg-gray-100 p-4 rounded-md shadow text-justify">
                    <i>“We are assured of continuous business with our customers overseas.”</i>
                    </p>
                    <p className="text-gray-700 mt-6 leading-relaxed bg-gray-100 p-4 rounded-md shadow text-justify">
                    <i>“We used to receive complaints and do reworks by the containers of returned goods. Now, we have the confidence to sell even to the most sensitive market in the world!”</i>
                    </p>
                    </div>
                </div>

                {/* Right Column: UDC Customer Focus */}
                <div className="w-full md:w-1/2 bg-gray-100 p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold text-[#0a354a]">UDC’s Absolute Customer Focus</h3>
                    <p className="text-gray-700 mt-4 leading-relaxed text-justify">
                    UDC conducts analysis on every trial shipment using <b>Top-Dri</b> to measure the humidity inside the container. 
                    Conducting this study is one of the services we offer to our valued customers, at no cost. Our team will be present 
                    during loading and unloading to inspect the effectiveness of <b>Top-Dri</b>.
                    </p>
                    <p className="text-gray-700 mt-4 leading-relaxed text-justify">
                    Our customers are then able to compare and analyze not only through physical inspection of their products upon arrival, 
                    but also with accurate temperature and humidity data recorded during the voyage. This allows them to measure cost reductions 
                    and determine the amount of desiccants needed for future shipments.
                    </p>
                    <p className="text-gray-700 mt-4 leading-relaxed text-justify">
                    We also provide comprehensive training about desiccants and their applications to key personnel in your company.
                    </p>

                    <p className="font-bold text-[#0a354a] mt-6 text-center">
                    MAKE UDC YOUR PARTNER IN MOISTURE CONTROL
                    </p>
                </div>
                </div>
            </div>
          )}

        </div>

      </div>
    </div>
  );
};

export default TopDriModal;
